import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template166: Actividades en París. Todo desde backend.
// page.texts[0]=header, [1]=intro | page.sections[0..n] (title, body, linkUrl, linkLabel, stickerImageId opcional en la última)
// page.images (sticker), page.canvasImages (logo)
const Template166 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const contentColor = textColor || "#444";

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found) return found.imageUrl ?? found.url;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const introText = page?.texts?.[1]?.content;
	const logoImage = getCanvasImageUrl("logo");

	const sections =
		Array.isArray(page?.sections)
			? page.sections
			: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
			? page.data.data.sections
			: [];
	const section0 = sections[0];
	const section1 = sections[1];
	const section2 = sections[2];
	const section3 = sections[3];
	const section4 = sections[4];
	const section5 = sections[5];

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const ff = fontFamily || "'Poppins', sans-serif";

	const h3Style = {
		fontFamily: `${ff} !important`,
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "8px",
		margin: 0,
		padding: 0,
		color: "#2C3E50",
		fontSize: 12,
	};

	const pStyle = {
		fontFamily: `${ff} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
		lineHeight: 1.5,
	};

	const boxStyle = {
		backgroundColor: "#E8F4F8",
		border: "2px solid #2C3E50",
		borderRadius: "16px",
		padding: "14px 16px",
	};

	const linkStyle = {
		color: "#2C3E50",
		textDecoration: "underline",
		fontWeight: 600,
		display: "inline-block",
		fontSize: 12,
	};

	const renderCard = (section, hasSticker) => {
		if (!section) return null;
		const stickerSrc = section.stickerImageId
			? getImageSrc(section.stickerImageId)
			: undefined;
		return (
			<Box
				sx={{ ...boxStyle, ...(hasSticker ? { position: "relative" } : {}) }}
			>
				{section.title && (
					<Box
						component="h3"
						sx={{
							...h3Style,
							fontSize: 12,
							...(hasSticker &&
								(isSmallScreen ? { width: "80%" } : { width: "90%" })),
						}}
					>
						{section.title}
					</Box>
				)}
				<Box
					component="p"
					sx={{
						...pStyle,
						...(hasSticker &&
							(isSmallScreen ? { width: "70%" } : { width: "85%" })),
					}}
				>
					{section.body}{" "}
					{section.linkUrl && (
						<Box
							component="a"
							href={section.linkUrl}
							target="_blank"
							rel="noopener noreferrer"
							sx={linkStyle}
						>
							{section.linkLabel || "Ver opciones"}
						</Box>
					)}
				</Box>
				{hasSticker && stickerSrc && (
					<img
						src={stickerSrc}
						alt=""
						style={{
							width: 100,
							position: "absolute",
							bottom: isSmallScreen ? -20 : 10,
							right: isSmallScreen ? -10 : -20,
						}}
					/>
				)}
			</Box>
		);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${ff} !important`,
				position: "relative",
				"& *:not([data-template-header])": { fontFamily: `${ff} !important`, color: "inherit" },
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "12px 16px", fontSize: 13, color: contentColor }}>
					<Box component="p" sx={pStyle}>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				data-block-id="text-2"
				sx={{
					padding: "0 16px 20px",
					display: "flex",
					flexDirection: "column",
					gap: "14px",
				}}
			>
				{renderCard(section0, false)}
				{renderCard(section1, false)}
				{renderCard(section2, false)}
				{renderCard(section3, false)}
				{renderCard(section4, false)}
				{renderCard(section5, true)}
			</Box>
		</Box>
	);
};

Template166.thumbnail = "/images/template-1.png";

export default Template166;
